import './firebase';
import React, { useEffect, useCallback, useMemo, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import useAuthStore from './store/authStore';
import { DarkModeProvider } from './contexts/DarkModeContext';

import Auth from './components/Auth';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './components/Home';
import { messages as sidebarMessages } from './components/chat/components/Sidebar';
// 🛡️ SECURITY: Removed direct Firestore imports - now using secure APIs
import useSetOnlineStatus from './hooks/useSetOnlineStatus';
import UseSetOnlineStatusGlobal from './hooks/UseSetOnlineStatusGlobal';
import Marketplace from './components/Marketplace';
import BuyYouTubeChannel from './components/BuyYouTubeChannel';
import BuyTiktokAccount from './components/BuyTiktokAccount';
import BuyFacebookPage from './components/BuyFacebookPage';
import BuyInstagramAccount from './components/BuyInstagramAccount';
import ProductView from './components/ProductView';
import Header from './components/Header';
import Footer from './components/Footer';
import SellAccount from './pages/SellAccount';
import EscrowService from './pages/EscrowService';
import ProfilePage from './pages/profile';
import WisePaymentDetails from './pages/WisePaymentDetails';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NotFound from './components/NotFound';
import Unauthorized from './components/Unauthorized';
import AdminLogin from './pages/AdminLogin';
import ResetPassword from './pages/ResetPassword';
import MobileFooterBar from './components/MobileFooterBar';
import GoogleAnalytics from './components/GoogleAnalytics';
import GoogleOneTap from './components/GoogleOneTap';
import FloatingChatIcon from './components/chat/components/FloatingChatIcon';
import RateLimitedRoute from './components/RateLimitedRoute';

// Admin site is heavy (charts, editor) – lazy-load to keep initial JS small
const AdminDashboardSite = React.lazy(() => import('./pages/admindashboardsite'));
// Lazy-load chat UI to keep initial bundle small on non-chat routes
const Sidebar = React.lazy(() => import('./components/chat/components/Sidebar'));
const ChatPanel = React.lazy(() => import('./components/chat/ChatPanel'));
const ProductDetail = React.lazy(() => import('./components/ProductDetail'));

// --- Minimal, production-only client error reporter ---
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: {
        location: window?.location?.pathname || '/',
        ...context,
      },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {
    // Do nothing – never throw or log in client reporter
  }
};

// --- Configurable admin email (now from .env) ---
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;
if (!ADMIN_EMAIL) {
  // Optionally log or throw an error in dev
}

// --- ErrorBoundary component ---
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    // Report minimal error details in production without exposing sensitive data
    safeReportError('ui_error_boundary', { error: error?.name || 'Error', info: 'render_failure' });
  }
  render() {
    if (this.state.hasError) {
      return <div style={{padding: 40, textAlign: 'center'}}>Something went wrong. Please refresh the page.</div>;
    }
    return this.props.children;
  }
}

// --- Lazy load heavy components ---
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const Checkout = React.lazy(() => import('./components/Checkout'));
const Blog = React.lazy(() => import('./components/Blog'));
const BlogDetail = React.lazy(() => import('./components/BlogDetail'));
const AboutUs = React.lazy(() => import('./components/AboutUs'));
const ContactUs = React.lazy(() => import('./components/ContactUs'));
const DeliveryFAQ = React.lazy(() => import('./components/DeliveryFAQ'));
const TermsAndConditions = React.lazy(() => import('./components/TermsAndConditions'));
const RefundAndReturnPolicy = React.lazy(() => import('./components/RefundAndReturnPolicy'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const Wishlist = React.lazy(() => import('./components/Wishlist'));
const Cart = React.lazy(() => import('./components/Cart'));

// Loading component for Suspense
const LoadingFallback = React.memo(() => (
  <div style={{padding: 40, textAlign: 'center'}}>Loading...</div>
));

// Memoized ChatApp component
const ChatApp = React.memo(() => {
  useSetOnlineStatus();
  const { user, isAdmin } = useAuthStore();
  const [selectedConversation, setSelectedConversation] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('transactions');
  const [transactions, setTransactions] = React.useState([]);
  const [messages, setMessages] = React.useState([]);
  const [agentMessages, setAgentMessages] = React.useState([]);
  const [sidebarLoading, setSidebarLoading] = React.useState(true);
  const [directConversationLoading, setDirectConversationLoading] = React.useState(false);
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get('room');
  const navigate = useNavigate();

  // Memoized handlers to prevent unnecessary re-renders
  const handleSelectConversation = useCallback((conv) => {
    setSelectedConversation(conv);
    
    // Auto-switch to appropriate tab based on conversation type
    if (conv.orderId || conv.order_id) {
      setActiveTab('transactions');
    } else {
      setActiveTab('messages');
    }
    
    if (conv && conv.id) {
      navigate(`/messages?room=${conv.id}`, { replace: false });
    }
  }, [navigate]);

  const updateAgentChatPreview = useCallback((text) => {
    setTransactions(prev => {
      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        lastMessage: text,
        lastMessageTime: Date.now(),
        unreadCount: 0,
      };
      return updated;
    });
    setMessages(prev => {
      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        lastMessage: text,
        lastMessageTime: Date.now(),
        unreadCount: 0,
      };
      return updated;
    });
  }, []);

  const moveConversationToTop = useCallback((convId, newMessage) => {
    setTransactions(prev => {
      const idx = prev.findIndex(c => c.id === convId);
      if (idx === -1) return prev;
      const updated = [...prev];
      const now = Date.now();
      updated[idx] = {
        ...updated[idx],
        lastMessage: newMessage,
        lastMessageTime: now,
        unreadCount: (updated[idx].unreadCount || 0) + 1,
      };
      const [item] = updated.splice(idx, 1);
      updated.unshift(item);
      return updated;
    });
  }, []);

  const updateMessagesPreview = useCallback((convId, newMessage) => {
    setMessages(prev => {
      const idx = prev.findIndex(c => c.id === convId);
      if (idx === -1) return prev;
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        lastMessage: newMessage,
        lastMessageTime: Date.now(),
        unreadCount: (updated[idx].unreadCount || 0) + 1,
      };
      // Move to top
      const [item] = updated.splice(idx, 1);
      updated.unshift(item);
      return updated;
    });
  }, []);

  // Memoized total unread count
  const totalUnread = useMemo(() => 
    transactions.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0), 
    [transactions]
  );

  // Memoized sidebar props
  const sidebarProps = useMemo(() => ({
    transactions,
    messages,
    moveConversationToTop,
    onSelectConversation: handleSelectConversation,
    selectedConversationId: selectedConversation?.id,
    activeTab,
    setActiveTab,
    totalUnread,
    sidebarWidth: 380,
    sidebarLoading,
  }), [transactions, messages, moveConversationToTop, handleSelectConversation, selectedConversation?.id, activeTab, setActiveTab, totalUnread, sidebarLoading]);

  // Memoized chat panel props
  const chatPanelProps = useMemo(() => ({
    conversation: selectedConversation,
    activeTab,
    moveConversationToTop,
    onSelectConversation: handleSelectConversation,
    transactions,
    agentMessages,
    setAgentMessages,
    updateAgentChatPreview,
    updateMessagesPreview,
    messages,
    setMessages,
    selectedConversationId: selectedConversation?.id,
  }), [selectedConversation, activeTab, moveConversationToTop, handleSelectConversation, transactions, agentMessages, setAgentMessages, updateAgentChatPreview, updateMessagesPreview, messages, setMessages]);

  // Remove artificial setTimeout, set loading to false after Firestore fetch
  React.useEffect(() => {
    setSidebarLoading(true);
    // Loading state will be set to false after Firestore fetch below
  }, []);

  // 🛡️ SECURITY: Fetch transaction conversations via secure API instead of direct Firestore
  React.useEffect(() => {
    if (!user || !user.email) return;
    
    // Security: Validate user email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(user.email)) {
      safeReportError('invalid_user_email_format');
      return;
    }
    
    setSidebarLoading(true);
    
    // 🛡️ SECURE: Use server-side API instead of direct Firestore access
    const fetchConversations = async () => {
      try {
        const userToken = await user.getIdToken();
        const response = await fetch('/api/conversations', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${userToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success && result.conversations) {
          setTransactions(result.conversations);
        } else {
          setTransactions([]);
          safeReportError('invalid_conversations_response_format');
        }
      } catch (error) {
        // Fallback to empty state
        setTransactions([{
          id: 'admin',
          name: 'Chat with Agent',
          date: new Date().toLocaleDateString(),
          online: true,
          avatar: 'https://i.pravatar.cc/40?img=1',
          lastMessage: 'How can I help you today?',
          unreadCount: 0,
          isAdmin: true
        }]);
      } finally {
      setSidebarLoading(false);
      }
    };

    fetchConversations();
    
    // Set up polling for real-time updates (every 30 seconds)
    const interval = setInterval(fetchConversations, 30000);
    
    return () => clearInterval(interval);
  }, [user]);

  // 🛡️ SECURITY: Handle roomId changes in URL with validation
  React.useEffect(() => {
    if (roomId && transactions.length > 0) {
      // Security: Validate roomId format (allowlist)
      const isValidRoomId = typeof roomId === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(roomId);
      if (!isValidRoomId) {
        safeReportError('invalid_room_id_format');
        navigate('/messages');
        return;
      }
      
      const targetConversation = transactions.find(conv => conv.id === roomId);

      if (targetConversation && (!selectedConversation || selectedConversation.id !== roomId)) {
        setSelectedConversation(targetConversation);
        
        // Auto-switch to appropriate tab based on conversation type
        if (targetConversation.orderId || targetConversation.order_id) {
          setActiveTab('transactions');
        } else {
          setActiveTab('messages');
        }
      }
    }
  }, [roomId, transactions, selectedConversation, navigate]);

  // Handle direct conversation access when not found in user's list
  React.useEffect(() => {
    if (roomId && !selectedConversation && transactions.length > 0) {
      const targetConversation = transactions.find(conv => conv.id === roomId);
      if (!targetConversation && roomId !== 'admin') {
        // 🛡️ SECURE: Try to fetch the conversation via secure API instead of direct Firestore
        setDirectConversationLoading(true);
        const fetchDirectConversation = async () => {
          try {
            const userToken = await user.getIdToken();
            const response = await fetch(`/api/conversations/${roomId}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const result = await response.json();
              
              if (result.success && result.conversation) {
                const convData = result.conversation;
                const formattedConversation = {
                  ...convData,
                  name: convData.seller?.name || convData.seller_name || 'Seller',
                  avatar: convData.seller_avatar || convData.seller?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(convData.seller?.name || convData.seller_name || 'Seller')}`,
                  date: convData.order_date || (convData.created_at && convData.created_at.toDate ? convData.created_at.toDate().toLocaleDateString() : ''),
                  unreadCount: 0
                };
                
                setSelectedConversation(formattedConversation);
                
                // Auto-switch to appropriate tab based on conversation type
                if (formattedConversation.orderId || formattedConversation.order_id) {
                  setActiveTab('transactions');
                } else {
                  setActiveTab('messages');
                }
                
                // Add to transactions list if not already there
                setTransactions(prev => {
                  if (!prev.find(conv => conv.id === roomId)) {
                    return [prev[0], formattedConversation, ...prev.slice(1)];
                  }
                  return prev;
                });
              } else {
                // Redirect to messages page without room parameter
                navigate('/messages');
              }
            } else if (response.status === 403) {
              // Access denied - redirect to messages page
              navigate('/messages');
            } else if (response.status === 404) {
              // Conversation not found - redirect to messages page
              navigate('/messages');
            } else {
              // Other error - redirect to messages page
              navigate('/messages');
            }
          } catch (error) {
            // Redirect to messages page without room parameter
            safeReportError('fetch_direct_conversation_failed');
            navigate('/messages');
          } finally {
            setDirectConversationLoading(false);
          }
        };
        fetchDirectConversation();
      }
    }
  }, [roomId, selectedConversation, transactions, user, navigate]);

  // 🛡️ SECURE: Add API-based listener for agent chat preview and unread count
  React.useEffect(() => {
    if (!user) return;
    
    // 🛡️ SECURE: Use secure API instead of direct Firestore
    const fetchAgentData = async () => {
      try {
        const userToken = await user.getIdToken();
        const response = await fetch(`/api/conversations/agent/${user.uid}/messages`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${userToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          
          if (result.success && result.messages) {
            const msgs = result.messages;
        if (msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          const agentMsgs = msgs.filter(m => m.from === 'admin' || m.from === ADMIN_EMAIL || m.name === 'YTproud Agent');
              
          setTransactions(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              lastMessage: lastMsg.text,
              lastMessageTime: Date.now(),
                  unreadCount: 0,
            };
            return updated;
          });
          setMessages(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              lastMessage: lastMsg.text,
              lastMessageTime: Date.now(),
                  unreadCount: 0,
            };
            return updated;
          });
        } else {
          setTransactions(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              lastMessage: 'How can I help you today?',
              lastMessageTime: Date.now(),
              unreadCount: 0,
            };
            return updated;
          });
          setMessages(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              lastMessage: 'How can I help you today?',
              lastMessageTime: Date.now(),
              unreadCount: 0,
            };
            return updated;
          });
        }
          }
        }
      } catch (error) {
        safeReportError('fetch_agent_messages_failed');
      }
    };

    fetchAgentData();
    
    // Set up polling for real-time updates (every 30 seconds)
    const interval = setInterval(fetchAgentData, 30000);
    
    return () => {
      clearInterval(interval);
    };
  }, [user, selectedConversation]);

  // Reset unread count when agent chat is selected/read and update Firestore
  React.useEffect(() => {
    if (selectedConversation && selectedConversation.id === 'admin') {
      // 🛡️ SECURE: Mark all as read via secure API instead of direct Firestore
      const convId = `agent_${user?.uid}`;
      
      // Get agent messages via secure API
      const updateAgentReadStatus = async () => {
        try {
          const userToken = await user.getIdToken();
          const response = await fetch(`/api/conversations/agent/${user.uid}/messages`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${userToken}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const result = await response.json();
            
            if (result.success && result.messages) {
              const msgs = result.messages;
        // Find the last agent message
        const agentMsgs = msgs.filter(m => m.from === 'admin' || m.from === ADMIN_EMAIL || m.name === 'YTproud Agent');
              
        if (agentMsgs.length > 0) {
          const lastAgentMsg = agentMsgs[agentMsgs.length - 1];
                
                // Update unread count in local state
          setTransactions(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              unreadCount: 0,
            };
            return updated;
          });
          setMessages(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              unreadCount: 0,
            };
            return updated;
          });
        }
            }
          }
        } catch (error) {
          safeReportError('update_agent_read_status_failed');
        }
      };

      updateAgentReadStatus();
    }
  }, [selectedConversation, user]);

  return (
    <div className="w-full h-screen bg-gray-100">
      <div className="flex h-full w-full bg-gray-100">
        {/* Sidebar - Hidden on mobile/tablet when conversation selected, visible on desktop */}
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <div className={`${selectedConversation ? 'hidden' : 'w-full'} xl:block`}>
              <Sidebar {...sidebarProps} />
            </div>
          </Suspense>
        </ErrorBoundary>
        {/* Chat Panel - Full width on mobile/tablet when conversation selected */}
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <div className={`${selectedConversation ? 'flex' : 'hidden'} xl:flex flex-1`}>
              <ChatPanel {...chatPanelProps} />
            </div>
          </Suspense>
        </ErrorBoundary>
        {/* Product Detail - Hidden on mobile/tablet, visible on desktop */}
        {selectedConversation && selectedConversation.id !== 'admin' && (
          <div className="hidden xl:block">
            <ProductDetail conversation={selectedConversation} isMobile={false} />
          </div>
        )}
      </div>
    </div>
  );
});

// Memoized App component
const App = React.memo(() => {
  const { initializeAuth } = useAuthStore();

  // Initialize authentication on app start
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return (
    <HelmetProvider>
    <DarkModeProvider>
    <Router>
      <UseSetOnlineStatusGlobal />
      <GoogleAnalytics />
      <GoogleOneTap />
      {/* Basic viewport and theme meta tags - SEO controlled via admin dashboard */}
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#3B82F6" />
        {/* Preconnect to external domains for faster loading */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        <link rel="preconnect" href="https://www.google.com" />
        <link rel="preconnect" href="https://apis.google.com" />
        <link rel="preconnect" href="https://firebase.googleapis.com" />
        <link rel="preconnect" href="https://identitytoolkit.googleapis.com" />
        <link rel="preconnect" href="https://securetoken.googleapis.com" />
        <link rel="preconnect" href="https://www.googleapis.com" />
        <link rel="preconnect" href="https://firebasestorage.googleapis.com" />
        <link rel="preconnect" href="https://lh3.googleusercontent.com" />
        <link rel="preconnect" href="https://i.pravatar.cc" />
        <link rel="preconnect" href="https://ui-avatars.com" />
        {/* DNS prefetch for other external domains */}
        <link rel="dns-prefetch" href="https://images.unsplash.com" />
        <link rel="dns-prefetch" href="https://ytproud.com" />
      </Helmet>
      <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/login" element={<Auth />} />
        <Route path="/signup" element={<Auth />} />
        <Route path="/" element={<Home />} />

        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <ChatApp />
            </ProtectedRoute>
          }
        />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/buy-youtube-channel" element={<BuyYouTubeChannel />} />
        <Route path="/buy-tiktok-account" element={<BuyTiktokAccount />} />
        <Route path="/buy-facebook-page" element={<BuyFacebookPage />} />
        <Route path="/buy-instagram-account" element={<BuyInstagramAccount />} />
        <Route path="/product/:slug" element={<ProductView />} />
        <Route 
          path="/escrowservice" 
          element={
            <>
              <Header />
              <EscrowService />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/sell-account" 
          element={
            <ProtectedRoute>
              <>
                <Header />
                <SellAccount />
                <Footer />
              </>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/about" 
          element={
            <>
              <Header />
              <AboutUs />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/contact" 
          element={
            <>
              <Header />
              <ContactUs />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/blog" 
          element={
            <>
              <Header />
              <Blog />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/blog/:slug" 
          element={
            <>
              <Header />
              <BlogDetail />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/delivery-faq" 
          element={
            <>
              <Header />
              <DeliveryFAQ />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/terms" 
          element={
            <>
              <Header />
              <TermsAndConditions />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/refund-policy" 
          element={
            <>
              <Header />
              <RefundAndReturnPolicy />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/privacy-policy" 
          element={
            <>
              <Header />
              <PrivacyPolicy />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/checkout" 
          element={
            <ProtectedRoute>
              <>
                <Header />
                <Checkout />
                <Footer />
              </>
            </ProtectedRoute>
          } 
        />
        <Route path={`/${process.env.REACT_APP_ADMIN_DASHBOARD_PATH || 'dashboard-a7f9b2e4-7c9e667d-6b93-11e7-9696-600308a46260'}`} element={
          <RateLimitedRoute maxRequests={5} windowMs={60000}>
          <ProtectedRoute requireAdmin={true}>
            <AdminDashboardSite />
          </ProtectedRoute>
          </RateLimitedRoute>
        } />
        <Route path={`/${process.env.REACT_APP_ADMIN_LOGIN_PATH || 'admin-x8k3m5n7-9f1d9504-6b93-11e7-9696-600308a46260'}`} element={
          <RateLimitedRoute maxRequests={3} windowMs={300000}>
            <AdminLogin />
          </RateLimitedRoute>
        } />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/profile/:username" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/wise-payment-details" element={<WisePaymentDetails />} />
        <Route 
          path="/wishlist" 
          element={
            <ProtectedRoute>
              <>
                <Header />
                <Wishlist />
                <Footer />
              </>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/cart" 
          element={
            <ProtectedRoute>
              <>
                <Header />
                <Cart />
                <Footer />
              </>
            </ProtectedRoute>
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      <ToastContainer
        position="top-center"
        autoClose={4000}
        toastClassName="!w-[420px] !min-h-[70px] !rounded-2xl !shadow-2xl !flex !items-center !border !overflow-hidden dark:!bg-[#1a1a1a] dark:!text-gray-100 dark:!border-gray-700 !bg-white !text-gray-900 !border-blue-200"
        bodyClassName="!text-lg !font-semibold !flex !items-center"
        progressClassName="!bg-green-500 dark:!bg-green-400 !h-1.5 !rounded-full"
        icon={<svg viewBox="0 0 24 24" className="text-red-500 dark:text-red-400 text-3xl mr-3" width="1em" height="1em" aria-hidden="true"><path fill="currentColor" d="M12 2a10 10 0 1 0 .001 20.001A10 10 0 0 0 12 2zm1 14h-2v-2h2v2zm0-4h-2V7h2v5z"/></svg>}
        closeButton={false}
        hideProgressBar={false}
        draggable={false}
        pauseOnHover
      />
      <MobileFooterBar />
      <FloatingChatIcon />
    </Router>
    </DarkModeProvider>
    </HelmetProvider>
  );
});

export default App;
